import { User } from '@auth/user';
import UserModel from '@auth/user/models/UserModel';
import { PartialDeep } from 'type-fest';
import apiFetch from '@/utils/apiFetch'; // ✅ Use apiFetch to work with MSW

/**
 * Sign in
 */
export async function authSignIn(credentials: { email: string; password: string }) {
  const response = await apiFetch('/account/login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  });
  return response.json();
}

/**
 * Sign in with token (Auto-login)
 */
export async function authSignInWithToken() {
  try {
    const response = await apiFetch('/account/validate-token', {
      method: 'GET'
    });
    const data = await response.json();

    if (!data || !data.user) {
      throw new Error('Invalid response structure from validate-token API');
    }

    return data.user; // ✅ Return only the user object
  } catch (error) {
    throw new Error(`Token validation failed: ${error.message}`);
  }
}

/**
 * Validate Session (Auto-login)
 */
export async function authValidate() {
  try {
    const response = await apiFetch('/account/validate-token', {
      method: 'GET'
    });
    const data = await response.json();

    if (!data || !data.user) {
      throw new Error('Invalid response structure from validate-token API');
    }

    return data.user; // ✅ Return only the user object
  } catch (error) {
    console.error('❌ Token validation failed:', error);
    throw new Error(`Token validation failed: ${error.message}`);
  }
}

/**
 * Request a password reset link. The API always responds 200 (it never reveals whether
 * the address exists), so callers should show the same confirmation regardless.
 */
export async function authForgotPassword(email: string) {
  const response = await apiFetch('/account/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email })
  });
  return response.json();
}

/**
 * Complete a password reset using the token + email from the emailed link.
 */
export async function authResetPassword(data: { email: string; token: string; newPassword: string }) {
  const response = await apiFetch('/account/reset-password', {
    method: 'POST',
    body: JSON.stringify(data)
  });
  return response.json();
}

/**
 * Sign out
 */
export async function authSignOut() {
  const response = await apiFetch('/account/sign-out', {
    method: 'POST'
  });
  return response.json();
}

/**
 * Refresh token
 */
export async function authRefreshToken() {
  const response = await apiFetch('/auth/refresh', {
    method: 'POST'
  });
  return response.json();
}

/**
 * Sign up
 */
export async function authSignUp(data: { displayName: string; email: string; password: string }) {
  const response = await apiFetch('/auth/sign-up', {
    method: 'POST',
    body: JSON.stringify(data)
  });
  return response.json();
}

/**
 * Get user by ID
 */
export async function authGetDbUser(userId: string) {
  const response = await apiFetch(`/auth/user/${userId}`, {
    method: 'GET'
  });
  return response.json();
}

/**
 * Get user by email
 */
export async function authGetDbUserByEmail(email: string) {
  const response = await apiFetch(`/account/user-by-email/${email}`, {
    method: 'GET'
  });
  return response.json();
}

/**
 * Update user
 */
export async function authUpdateDbUser(user: PartialDeep<User>) {
  const response = await apiFetch(`/settings/account-settings`, {
    method: 'PUT',
    body: JSON.stringify(UserModel(user))
  });
  return response.json();
}

/**
 * Create user
 */
export async function authCreateDbUser(user: PartialDeep<User>) {
  const response = await apiFetch('/users', {
    method: 'POST',
    body: JSON.stringify(UserModel(user))
  });
  return response.json();
}

/**
 * Delete workflow item
 */
export async function deleteWorkflowItem(id: string) {
  const response = await apiFetch(`/workflows/items/${id}`, {
    method: 'DELETE'
  });
  return response.json();
}
